import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Plus, Trash2, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const empty = { name: "", address: "", city: "Salt Lake City, UT", lat: 0, lng: 0, description: "" };
const card = "bg-white dark:bg-neutral-900 border border-brand-line dark:border-neutral-800 rounded-2xl";

export default function Locations() {
  const [locs, setLocs] = useState(null);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/locations").then((r) => setLocs(r.data)).catch(() => setLocs([]));
  useEffect(() => { load(); }, []);

  const set = (k, num) => (e) => setForm({ ...form, [k]: num ? parseFloat(e.target.value || 0) : e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/admin/locations/${editing}`, form); toast.success("Location updated"); }
      else { await api.post("/admin/locations", form); toast.success("Location added"); }
      setForm(empty); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const edit = (l) => { setForm({ name: l.name, address: l.address, city: l.city, lat: l.lat, lng: l.lng, description: l.description || "" }); setEditing(l.id); };
  const remove = async (l) => {
    if (!window.confirm(`Delete ${l.name}?`)) return;
    try { await api.delete(`/admin/locations/${l.id}`); toast.success("Deleted"); if (editing === l.id) { setForm(empty); setEditing(null); } load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="locations-page">
      <h1 className="font-display text-4xl">Locations</h1>
      <p className="text-brand-ink/60 dark:text-white/50 mt-2">Manage the parks you teach at. These can be assigned to classes.</p>
      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <form onSubmit={save} className={`${card} p-6 space-y-3 lg:col-span-1 h-fit`} data-testid="location-form">
          <h2 className="font-display text-xl">{editing ? "Edit location" : "Add location"}</h2>
          <In label="Park name" v={form.name} on={set("name")} testid="loc-name" required />
          <In label="Address" v={form.address} on={set("address")} testid="loc-address" />
          <In label="City" v={form.city} on={set("city")} testid="loc-city" />
          <div className="grid grid-cols-2 gap-3">
            <In label="Latitude" type="number" v={form.lat} on={set("lat", true)} testid="loc-lat" />
            <In label="Longitude" type="number" v={form.lng} on={set("lng", true)} testid="loc-lng" />
          </div>
          <In label="Description" v={form.description} on={set("description")} testid="loc-desc" />
          <div className="flex items-center gap-3 pt-1">
            <button type="submit" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors" data-testid="location-save-btn">{editing ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {editing ? "Update" : "Add location"}</button>
            {editing && <button type="button" onClick={() => { setForm(empty); setEditing(null); }} className="text-sm text-brand-ink/50 hover:text-brand-red">Cancel</button>}
          </div>
        </form>
        <div className="lg:col-span-2 space-y-3">
          {!locs ? <div className="text-brand-ink/50">Loading…</div> : locs.length === 0 ? <div className="text-brand-ink/50 dark:text-white/40" data-testid="locations-empty">No locations yet.</div> : locs.map((l) => (
            <div key={l.id} className={`${card} p-5 flex items-center justify-between gap-3`} data-testid={`location-${l.id.slice(0, 8)}`}>
              <div>
                <div className="font-medium flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-red" /> {l.name}</div>
                <div className="text-sm text-brand-ink/50 dark:text-white/40 mt-0.5">{l.address}{l.city ? ` · ${l.city}` : ""}</div>
                {l.description && <div className="text-sm text-brand-ink/60 dark:text-white/50 mt-1">{l.description}</div>}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button onClick={() => edit(l)} className="text-sm text-brand-red hover:underline" data-testid={`loc-edit-${l.id.slice(0, 8)}`}>Edit</button>
                <button onClick={() => remove(l)} className="text-brand-ink/40 hover:text-brand-red" data-testid={`loc-delete-${l.id.slice(0, 8)}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const In = ({ label, v, on, type = "text", testid, required }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.15em] text-brand-ink/50 dark:text-white/40 font-semibold">{label}</label>
    <input type={type} value={v} onChange={on} required={required} step={type === "number" ? "any" : undefined} className="mt-1.5 w-full rounded-lg border border-brand-line dark:border-neutral-700 dark:bg-neutral-900 px-3 py-2 text-sm focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid={testid} />
  </div>
);
